import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { key: 1 }

import { computed, PropType, ref } from "vue";

import { useContentStore } from "@/stores/content.store";
import { useUIGeneralStore } from "@/stores/ui-general.store";

import { ContentStructureSection } from "@/models/content-structure.model";
import {
  ConceptJson,
  ConceptsByTopic,
  TopicJson
} from "@/models/curriculum.model";
import { SectionConcepts } from "@/models/product-section.model";

import TagsList from "@/components/right-sidebars/content-sidebar/curriculum/TagsList.vue";
import Collapse from "@/components/right-sidebars/ui/Collapse.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'CurriculumTags',
  props: {
  section: {
    type: Object as PropType<ContentStructureSection>,
    required: true
  },
  productCurriculumId: {
    type: Number,
    required: true
  },
  isLoading: {
    type: Boolean,
    required: true
  },
  topicsFromXml: {
    type: Array as PropType<TopicJson[]>,
    default: () => []
  },
  conceptsFromXml: {
    type: Array as PropType<ConceptJson[]>,
    default: () => []
  },
  xmlParsedSuccessfully: {
    type: Boolean,
    default: false
  },
  canEditContent: {
    type: Boolean,
    required: true,
    default: false
  }
},
  setup(__props) {

const uiGeneralStore = useUIGeneralStore();
const contentStore = useContentStore();

const props = __props;

const typedConcept = ref<string>("");

const sectionTagsFromSelectedCurriculum = computed<SectionConcepts[]>(() => {
  if (!Object.prototype.hasOwnProperty.call(props.section, "concepts")) {
    return [];
  }
  const conceptsFromXmlGuids = props.conceptsFromXml.map(
    (conceptXml: ConceptJson) => conceptXml.guid
  );
  return props.section.concepts.filter((sectionConcept: SectionConcepts) => {
    return conceptsFromXmlGuids.includes(sectionConcept.concept_guid);
  });
});
const sectionHaveTagsButDoesntHaveTagsFromSelectedCurriculum =
  computed<boolean>(() => {
    return (
      props.section.concepts.length > 0 &&
      sectionTagsFromSelectedCurriculum.value.length === 0
    );
  });
const sectionTagsWithNames = computed<SectionConcepts[]>(() => {
  return sectionTagsFromSelectedCurriculum.value.map(
    (sectionConcept: SectionConcepts) => {
      const conceptName = props.conceptsFromXml.find(
        (conceptFromXml: ConceptJson) => {
          return conceptFromXml.guid === sectionConcept.concept_guid;
        }
      );
      if (conceptName) {
        sectionConcept.name = conceptName.name;
      }
      return sectionConcept;
    }
  );
});
const conceptsByTopicsFromXml = computed<ConceptsByTopic[]>(() => {
  const conceptsByTopics: ConceptsByTopic[] = [];
  props.topicsFromXml.forEach((topic: TopicJson) => {
    const item: ConceptsByTopic = {
      topicName: topic.name,
      topicFullData: topic,
      conceptsNames: [],
      conceptsFullData: []
    };
    props.conceptsFromXml.forEach((concept: ConceptJson) => {
      if (topic.guid === concept.parentGuid) {
        item.conceptsNames.push(concept.name);
        item.conceptsFullData.push(concept);
      }
    });
    conceptsByTopics.push(item);
  });
  return conceptsByTopics;
});
const filteredConceptsByTopics = computed<ConceptsByTopic[]>(() => {
  if (!typedConcept.value.length) {
    return conceptsByTopicsFromXml.value;
  }
  const filteredConceptsByTopics: ConceptsByTopic[] = [];
  conceptsByTopicsFromXml.value.forEach((element: ConceptsByTopic) => {
    const conceptsNames = element.conceptsNames.filter(
      (conceptName: string) => {
        return (
          conceptName.toLowerCase().indexOf(typedConcept.value.toLowerCase()) >=
          0
        );
      }
    );
    if (conceptsNames.length) {
      filteredConceptsByTopics.push({
        topicName: element.topicName,
        topicFullData: element.topicFullData,
        conceptsNames: conceptsNames,
        conceptsFullData: element.conceptsFullData
      });
    }
  });
  return filteredConceptsByTopics;
});

const message = computed<string>(() => {
  if (props.isLoading) {
    return "Loading...";
  }
  if (!props.xmlParsedSuccessfully) {
    return "Curriculum file is invalid";
  }
  if (!conceptsByTopicsFromXml.value.length) {
    return "Curriculum file has no topics or concepts";
  }
  if (sectionHaveTagsButDoesntHaveTagsFromSelectedCurriculum.value) {
    return "This section doesn't have tags from selected curriculum, but have tags from another curriculum.";
  }
  return "";
});
const showTags = computed<boolean>(() => {
  return (
    !props.isLoading &&
    props.xmlParsedSuccessfully &&
    conceptsByTopicsFromXml.value.length > 0 &&
    !!sectionTagsWithNames.value &&
    (sectionTagsWithNames.value.length > 0 ||
      (sectionTagsWithNames.value.length === 0 &&
        uiGeneralStore.isEditModeEnabled))
  );
});
const showConceptsSelector = computed<boolean>(() => {
  return (
    !props.isLoading &&
    props.xmlParsedSuccessfully &&
    !!conceptsByTopicsFromXml.value.length &&
    props.canEditContent
  );
});

const getConceptByName = (conceptName: string): ConceptJson | undefined => {
  return props.conceptsFromXml.find((concept: ConceptJson) => {
    return concept.name === conceptName;
  });
};
const addConceptToSection = (concept: ConceptJson): void => {
  const payload = {
    concept_guid: concept.guid,
    section_id: props.section.id
  };
  contentStore.addConceptToSection(payload, props.section.product_id);
};
const removeConceptFromSection = (conceptId: number): void => {
  contentStore.removeConceptFromSection(conceptId, props.section.product_id);
};
const processSelectedConcept = (conceptName: string): void => {
  const selectedConcept = getConceptByName(conceptName);
  const selectedSectionConceptsGuids = props.section.concepts.map(
    (concept: SectionConcepts) => concept.concept_guid
  );
  if (
    selectedConcept &&
    !selectedSectionConceptsGuids.includes(selectedConcept.guid)
  ) {
    addConceptToSection(selectedConcept);
  }
};

return (_ctx: any,_cache: any) => {
  const _component_b_autocomplete = _resolveComponent("b-autocomplete")!
  const _component_b_field = _resolveComponent("b-field")!

  return (_openBlock(), _createBlock(Collapse, null, {
    title: _withCtx(() => [...(_cache[1] || (_cache[1] = [
      _createTextVNode("Curriculum Tags", -1)
    ]))]),
    content: _withCtx(() => [
      (showConceptsSelector.value)
        ? (_openBlock(), _createBlock(_component_b_field, {
            key: 0,
            label: "Concepts"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_b_autocomplete, {
                modelValue: typedConcept.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((typedConcept).value = $event)),
                data: filteredConceptsByTopics.value,
                onSelect: processSelectedConcept,
                placeholder: "e.g. Adding fractions",
                "group-field": "topicName",
                "group-options": "conceptsNames",
                "confirm-keys": ['Enter'],
                "keep-first": "",
                "open-on-focus": "",
                clearable: "",
                "clear-on-select": ""
              }, null, 8, ["modelValue", "data"])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (!showConceptsSelector.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(message.value), 1))
        : _createCommentVNode("", true),
      (showTags.value)
        ? (_openBlock(), _createBlock(TagsList, {
            key: 2,
            tags: sectionTagsWithNames.value,
            onRemoveTag: removeConceptFromSection,
            "can-edit-content": __props.canEditContent
          }, null, 8, ["tags", "can-edit-content"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})